/** Web Push: zgoda przeglądarki + subskrypcja trzymana przez backend. */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { currentState, disablePush, enablePush, type PushState } from "./push";
import { settingsKeys } from "./SettingsPage";

const LABEL: Record<PushState, string> = {
  unsupported: "Ta przeglądarka nie obsługuje powiadomień push.",
  denied: "Powiadomienia zablokowane w ustawieniach przeglądarki.",
  off: "Powiadomienia wyłączone.",
  on: "Powiadomienia włączone.",
};

export function PushSection() {
  const [state, setState] = useState<PushState>("off");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState<string | null>(null);

  const config = useQuery({ queryKey: settingsKeys.pushConfig, queryFn: api.pushConfig });

  useEffect(() => {
    void currentState().then(setState);
  }, []);

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      setState(state === "on" ? await disablePush() : await enablePush());
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setTested(null);
    try {
      const result = await api.pushTest();
      setTested(result.skipped_reason ?? `Wysłano do ${result.sent} urządzeń.`);
    } catch (exc) {
      setTested(exc instanceof Error ? exc.message : String(exc));
    }
  }

  const disabled = busy || state === "unsupported" || state === "denied";

  return (
    <section className="card settings__section">
      <h2 className="card__title">Powiadomienia</h2>
      <p className="muted">
        Agent budzi telefon, gdy appka jest zamknięta — ceremonie i przypomnienia
        (PLAN §9: push tylko bez aktywnego połączenia albo dla ważnych wiadomości).
      </p>

      <p className="muted">{LABEL[state]}</p>
      {config.data && !config.data.enabled && (
        <p className="error">
          Backend nie ma kluczy VAPID. Uruchom <code>python -m app.push_keys</code> i wklej je do
          <code> .env</code>.
        </p>
      )}
      {error && <p className="error">{error}</p>}

      <div className="clarify__buttons">
        <button className="btn btn--primary" disabled={disabled} onClick={() => void toggle()}>
          {state === "on" ? "Wyłącz powiadomienia" : "Włącz powiadomienia"}
        </button>
        <button className="btn" disabled={state !== "on"} onClick={() => void test()}>
          Wyślij testowe
        </button>
      </div>
      {tested && <p className="muted">{tested}</p>}
      {config.data && (
        <p className="muted">Zarejestrowanych urządzeń: {config.data.subscriptions}</p>
      )}
    </section>
  );
}
